/* eslint-disable import/no-mutable-exports */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-restricted-syntax */
import { ERROR, SECRET_TYPE } from './constants'
import { checkIsInScf, getEnv } from './utils'
import type { IContextParam, ICompleteCloudbaseContext } from './types'
import { ICloudbaseConfig } from '@cloudbase/types'

/**
 * 解析并校验云函数入口的 Context 参数
 * 将下划线命名的字段转为驼峰格式，并解析环境变量
 *
 * @param context - 云函数入口传入的 context 对象
 * @returns 解析后的上下文对象，包含 environment（新架构）或 environ（老架构）
 * @throws INVALID_CONTEXT - context 不是对象或解析失败时
 */
export function parseContext(context: IContextParam) {
  if (typeof context !== 'object') {
    throw { ...ERROR.INVALID_CONTEXT, message: 'context 必须为对象类型' }
  }

  const parseResult: any = {}

  const {
    memory_limit_in_mb,
    time_limit_in_ms,
    request_id,
    function_version,
    namespace,
    function_name,

    environ,
    environment,
  } = context

  try {
    parseResult.memoryLimitInMb = memory_limit_in_mb
    parseResult.timeLimitIns = time_limit_in_ms
    parseResult.requestId = request_id
    parseResult.functionVersion = function_version
    parseResult.namespace = namespace
    parseResult.functionName = function_name

    if (environment) {
      // 新架构：environment 为 JSON 字符串，直接解析
      parseResult.environment = JSON.parse(environment)
    } else {
      // TODO: 考虑移除老架构的兼容逻辑
      // 老架构：environ 为分号分隔的 key=value 字符串
      // 存在 bug：value 含特殊字符时可能影响解析
      const parseEnviron: any = environ?.split(';')
      const parseEnvironObj: any = {}

      // eslint-disable-next-line @typescript-eslint/no-for-in-array
      for (const i in parseEnviron) {
        if (parseEnviron[i].includes('=')) {
          // 只按第一个 = 切割，保留 value 中的 =
          const equalIndex = parseEnviron[i].indexOf('=')
          const key = parseEnviron[i].slice(0, equalIndex)
          let value: any = parseEnviron[i].slice(equalIndex + 1)

          // value 含逗号时视为数组
          if (value.indexOf(',') >= 0) {
            value = value.split(',')
          }
          parseEnvironObj[key] = value
        }
      }

      parseResult.environ = parseEnvironObj
    }
  } catch (_err: any) {
    throw { ...ERROR.INVALID_CONTEXT }
  }

  return parseResult
}

/**
 * 获取当前云函数内的所有环境变量
 * 统一从 process.env 和 context 两个来源合并取值
 *
 * 取值优先级：context > process.env
 * 通过 TCB_CONTEXT_KEYS / WX_CONTEXT_KEYS 动态扩展需要读取的环境变量列表
 *
 * @param context - 可选，云函数入口的 context 参数
 * @returns 合并后的完整环境变量上下文
 */
export const getCloudbaseContext = (context?: IContextParam): ICompleteCloudbaseContext => {
  // 云函数环境下校验必要环境变量
  if (checkIsInScf()) {
    if (!getEnv('TENCENTCLOUD_REGION')) {
      console.error('[TCB][ERROR] missing `TENCENTCLOUD_REGION` environment')
    }
    if (!getEnv('SCF_NAMESPACE')) {
      console.error('[TCB][ERROR] missing `SCF_NAMESPACE` environment')
    }
  }

  // 第一步：从 process.env 读取基础环境变量
  const envObj = getEnv()
  const envFromProcessEnv: any = {
    TRIGGER_SRC: envObj.TRIGGER_SRC,
    _SCF_TCB_LOG: envObj._SCF_TCB_LOG,
    SCF_NAMESPACE: envObj.SCF_NAMESPACE,
    TENCENTCLOUD_RUNENV: envObj.TENCENTCLOUD_RUNENV,
    TENCENTCLOUD_SECRETID: envObj.TENCENTCLOUD_SECRETID,
    TENCENTCLOUD_SECRETKEY: envObj.TENCENTCLOUD_SECRETKEY,
    TENCENTCLOUD_SESSIONTOKEN: envObj.TENCENTCLOUD_SESSIONTOKEN,
    WX_CONTEXT_KEYS: envObj.WX_CONTEXT_KEYS,
    WX_TRIGGER_API_TOKEN_V0: envObj.WX_TRIGGER_API_TOKEN_V0,
    WX_CLIENTIP: envObj.WX_CLIENTIP,
    WX_CLIENTIPV6: envObj.WX_CLIENTIPV6,
    TCB_CONTEXT_KEYS: envObj.TCB_CONTEXT_KEYS,
    TCB_CONTEXT_CNFG: envObj.TCB_CONTEXT_CNFG,
    LOGINTYPE: envObj.LOGINTYPE,
    CLOUDBASE_APIKEY: envObj.CLOUDBASE_APIKEY,
  }

  // 第二步：从 context 中解析环境变量
  let envFromContext: any = {}
  if (context) {
    const { environment, environ } = parseContext(context)
    envFromContext = environment || environ || {}
  }

  // 第三步：根据 CONTEXT_KEYS 动态扩展变量列表（优先取 context 中的值）
  const tcbContextKeys = envFromContext.TCB_CONTEXT_KEYS || envObj.TCB_CONTEXT_KEYS
  const wxContextKeys = envFromContext.WX_CONTEXT_KEYS || envObj.WX_CONTEXT_KEYS

  // 根据 CONTEXT_KEYS 动态扩展变量列表
  for (const contextKeys of [tcbContextKeys, wxContextKeys]) {
    if (contextKeys) {
      try {
        for (const item of contextKeys.split(',')) {
          envFromProcessEnv[item] = envFromContext[item] || getEnv(item)
        }
      } catch (_e) {
        //
      }
    }
  }

  // 第四步：合并所有来源，context 的值覆盖 process.env，过滤 undefined
  const allContext = { ...envFromProcessEnv, ...envFromContext }

  return Object.fromEntries(Object.entries(allContext).filter(([, v]) => v !== undefined)) as any
}

/**
 * 获取 TCB_CONTEXT_CNFG 配置
 * 解析环境变量中约定的 JSON 配置
 */
export function getTcbContextConfig(): any {
  try {
    const context: any = getCloudbaseContext()
    const { TCB_CONTEXT_CNFG } = context
    if (TCB_CONTEXT_CNFG) {
      return JSON.parse(TCB_CONTEXT_CNFG)
    }
    return {}
  } catch (_e) {
    console.error('[TCB][ERROR] parse context error: ', _e)
    return {}
  }
}

/**
 * 获取服务端注入的 URL
 */
export function getServerInjectUrl(): string {
  const tcbContextConfig = getTcbContextConfig()
  return tcbContextConfig.URL || ''
}

/** 缓存 extendedContext，供其他模块读取（如 tool.ts 中获取 userId） */
export let INIT_CONFIG: ICloudbaseConfig = {} as unknown as ICloudbaseConfig

/**
 * 从环境变量 / config 顶层字段 / context 中获取腾讯云密钥信息
 * 兼容 node-sdk 风格的顶层配置（secretId, secretKey, credentials, context 等）
 * 用于 API 请求签名
 */
export const getSecretInfo = (config?: ICloudbaseConfig) => {
  INIT_CONFIG = JSON.parse(JSON.stringify(config || INIT_CONFIG))

  // --- 环境变量中的密钥 ---
  const {  TCB_ENV, SCF_NAMESPACE, TENCENTCLOUD_RUNENV, TENCENTCLOUD_SECRETID, TENCENTCLOUD_SECRETKEY, TENCENTCLOUD_SESSIONTOKEN, CLOUDBASE_APIKEY } = getCloudbaseContext()
  const topLevel = { ...INIT_CONFIG, ...config } as any

  // --- env 处理 ---
  // env 可能为 symbol（SYMBOL_CURRENT_ENV / SYMBOL_DEFAULT_ENV），尝试从环境变量获取
  let env = ''
  if (typeof config?.env === 'symbol' || !config?.env) {
    env = TCB_ENV || SCF_NAMESPACE || ''
  } else {
    env = config.env as string
  }

  // 本地调试环境特殊处理
  if (env === 'local' && TENCENTCLOUD_RUNENV === 'WX_LOCAL_SCF') {
    env = ''
  }

  const defaultInfo = {
    env,
    secretId: '',
    secretKey: '',
    sessionToken: '',
    accessKey: '',
    secretType: '',
    credentials: undefined as any,
    context: undefined as any,
  }

  // --- 顶层 context（functions-framework 上下文） ---
  if (topLevel?.context) {
    defaultInfo.context = topLevel.context
  }

  // --- credentials（私钥认证）---
  // 兼容两种传法：node-sdk 风格的顶层 credentials，以及 js-sdk 风格的 config.auth.credentials
  if (topLevel?.credentials || config?.auth?.credentials) {
    defaultInfo.credentials = topLevel?.credentials || config?.auth?.credentials
  }

  // --- accessKey 优先级：config.accessKey > 环境变量 ---
  if (config?.accessKey || topLevel?.accessKey) {
    defaultInfo.accessKey = config?.accessKey || topLevel?.accessKey
    return defaultInfo
  }

  // --- 顶层 secretId/secretKey（node-sdk 风格兼容） ---
  if (topLevel?.secretId && topLevel?.secretKey) {
    return {
      ...defaultInfo,
      secretId: topLevel.secretId,
      secretKey: topLevel.secretKey,
      sessionToken: topLevel.sessionToken || '',
      secretType: topLevel.sessionToken ? SECRET_TYPE.SESSION_SECRET : SECRET_TYPE.SECRET,
    }
  }

  // --- config.auth 中的密钥 ---
  if (config?.auth?.secretId && config?.auth?.secretKey) {
    return {
      ...defaultInfo,
      secretId: config.auth.secretId,
      secretKey: config.auth.secretKey,
      sessionToken: config.auth.sessionToken || '',
      secretType: config.auth.secretType || SECRET_TYPE.SECRET,
    }
  }

  if (CLOUDBASE_APIKEY) {
    return {
      ...defaultInfo,
      accessKey: CLOUDBASE_APIKEY,
    }
  }

  if (TENCENTCLOUD_SECRETID && TENCENTCLOUD_SECRETKEY) {
    return {
      ...defaultInfo,
      secretId: TENCENTCLOUD_SECRETID,
      secretKey: TENCENTCLOUD_SECRETKEY,
      sessionToken: TENCENTCLOUD_SESSIONTOKEN || '',
      secretType: SECRET_TYPE.SESSION_SECRET,
    }
  }

  // --- context.extendedContext 中的临时密钥 ---
  const contextConfig = topLevel?.context || config?.context
  if (contextConfig?.extendedContext) {
    const { extendedContext } = contextConfig
    if (!env) {
      env = extendedContext.envId || ''
    }

    return {
      ...defaultInfo,
      env,
      secretId: extendedContext?.tmpSecret?.secretId || '',
      secretKey: extendedContext?.tmpSecret?.secretKey || '',
      sessionToken: extendedContext?.tmpSecret?.token || '',
    }
  }

  return { ...defaultInfo }
}
